import { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InquiryPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    event_type: "",
    event_date: "",
    location: "",
    guest_count: "",
    desired_atmosphere: "",
    additional_details: "",
    referral_source: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/inquiries`, formData);
      if (response.data.success) {
        setSubmitStatus({
          type: "success",
          message: response.data.message,
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          event_type: "",
          event_date: "",
          location: "",
          guest_count: "",
          desired_atmosphere: "",
          additional_details: "",
          referral_source: "",
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "Unable to submit your inquiry at this time. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventTypes = [
    "Private Wedding",
    "Corporate Event",
    "Private Estate Gathering",
    "Gala or Fundraiser",
    "Boutique Venue Showcase",
    "Private Party",
    "Other",
  ];

  const guestCounts = [
    "Under 50",
    "50 - 100",
    "100 - 200",
    "200 - 300",
    "300+",
  ];

  return (
    <div data-testid="inquiry-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1651667773148-9283495a32b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwd2VkZGluZyUyMHJlY2VwdGlvbiUyMHZlbnVlJTIwZGltbHklMjBsaXR8ZW58MHx8fHwxNzY5MzI1NDQwfDA&ixlib=rb-4.1.0&q=85"
            alt="Elegant venue"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F] via-transparent to-[#0F0F0F]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6 animate-fade-in">
            Begin the Conversation
          </p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F4F2EE] mb-6 animate-fade-in animate-delay-100" data-testid="inquiry-heading">
            Request Availability
          </h1>
          <p className="max-w-2xl mx-auto text-[#F4F2EE]/60 leading-relaxed animate-fade-in animate-delay-200">
            Every engagement is thoughtfully considered. Please provide details about your event, and expect a response within 48 hours.
          </p>
        </div>
      </section>

      {/* Inquiry Info */}
      <section className="py-16 px-6 lg:px-12 bg-[#0A0A0A]" data-testid="inquiry-info-section">
        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-2">
                Pricing
              </p>
              <p className="text-sm text-[#F4F2EE]/60">
                Event-based pricing tailored to scope
              </p>
            </div>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-2">
                Availability
              </p>
              <p className="text-sm text-[#F4F2EE]/60">
                Limited monthly engagements
              </p>
            </div>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-2">
                Response Time
              </p>
              <p className="text-sm text-[#F4F2EE]/60">
                Within 48 hours
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 px-6 lg:px-12" data-testid="inquiry-form-section">
        <div className="max-w-3xl mx-auto">
          {submitStatus?.type === "success" ? (
            <div className="text-center py-16" data-testid="success-message">
              <div className="w-16 h-16 border border-[#B8A369] mx-auto mb-8 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-[#B8A369]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="font-serif text-3xl text-[#F4F2EE] mb-4">
                Inquiry Received
              </h2>
              <p className="text-[#F4F2EE]/60 leading-relaxed max-w-md mx-auto">
                {submitStatus.message}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="inquiry-form">
              {/* Contact Information */}
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
                  Contact Information
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label" htmlFor="name">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="email">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="phone">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="company">
                      Company / Planning Firm
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-company"
                    />
                  </div>
                </div>
              </div>

              <div className="line-gold" />

              {/* Event Details */}
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
                  Event Details
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label" htmlFor="event_type">
                      Event Type *
                    </label>
                    <select
                      id="event_type"
                      name="event_type"
                      value={formData.event_type}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="select-event-type"
                    >
                      <option value="">Select event type</option>
                      {eventTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label" htmlFor="event_date">
                      Event Date *
                    </label>
                    <input
                      type="date"
                      id="event_date"
                      name="event_date"
                      value={formData.event_date}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="input-event-date"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="location">
                      Location / Venue *
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      placeholder="City, State or Venue Name"
                      className="form-input"
                      data-testid="input-location"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="guest_count">
                      Anticipated Guest Count *
                    </label>
                    <select
                      id="guest_count"
                      name="guest_count"
                      value={formData.guest_count}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="select-guest-count"
                    >
                      <option value="">Select guest count</option>
                      {guestCounts.map((count) => (
                        <option key={count} value={count}>
                          {count}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="line-gold" />

              {/* Atmosphere */}
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-6">
                  Desired Atmosphere
                </p>
                <div>
                  <label className="form-label" htmlFor="desired_atmosphere">
                    Describe the atmosphere you envision *
                  </label>
                  <textarea
                    id="desired_atmosphere"
                    name="desired_atmosphere"
                    value={formData.desired_atmosphere}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="What feeling should the music create? Background ambiance, focal point, transitional moments..."
                    className="form-input resize-none"
                    data-testid="textarea-atmosphere"
                  />
                </div>
              </div>

              {/* Additional Details */}
              <div>
                <label className="form-label" htmlFor="additional_details">
                  Additional Details
                </label>
                <textarea
                  id="additional_details"
                  name="additional_details"
                  value={formData.additional_details}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Any specific requirements, timeline considerations, or other details..."
                  className="form-input resize-none"
                  data-testid="textarea-additional"
                />
              </div>

              {/* Referral */}
              <div>
                <label className="form-label" htmlFor="referral_source">
                  How did you hear about us?
                </label>
                <input
                  type="text"
                  id="referral_source"
                  name="referral_source"
                  value={formData.referral_source}
                  onChange={handleChange}
                  placeholder="Referral, venue recommendation, etc."
                  className="form-input"
                  data-testid="input-referral"
                />
              </div>

              {/* Submit */}
              {submitStatus?.type === "error" && (
                <div className="text-center py-4" data-testid="error-message">
                  <p className="text-sm text-red-400">{submitStatus.message}</p>
                </div>
              )}

              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-luxury disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Submit Inquiry"}
                </button>
                <p className="mt-6 text-xs text-[#F4F2EE]/40">
                  All inquiries are reviewed personally. Expect a response within 48 hours.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Note Section */}
      <section className="py-16 px-6 lg:px-12 bg-[#0A0A0A]" data-testid="note-section">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8A369] mb-4">
            Please Note
          </p>
          <p className="text-sm text-[#F4F2EE]/50 leading-relaxed">
            Due to the personalized nature of each engagement, availability is intentionally limited. Early inquiry is recommended for preferred dates. Not all inquiries will result in booking, as fit between artist and event is carefully considered.
          </p>
        </div>
      </section>
    </div>
  );
};

export default InquiryPage;
